import React from 'react';
import cx from 'classnames';

import { RadioButton } from '@trqk/components';
import { getGapDateRangesFromPeriodSelection } from '@trqk/utils';

import { DetectionBar, PerformanceBar } from '.';
import { columnWidth, rowHeight } from '../constants';

interface ReportPeriodColumnProps {
    hasDetectionData: boolean;
    hasPerformanceData: boolean;
    label: string;
    onSelectGapAnalysisPeriod: OnSelectGapAnalysisPeriod;
    periodSelection: GapAnalysisPeriodSelection;
    selectedPeriod: GapAnalysisPeriodSelection | null;
}

/**
 * Allows the user to select a report period in order to perform a Gap Analysis report. Shows
 * details for a performance period, including the period label (such as 'Q1 2021') and two bars
 * that indicate whether it contains performances and detections.
 * @param props - {@link ReportPeriodColumnProps}
 * @returns JSX
 */
export const ReportPeriodColumn = ({
    hasDetectionData,
    hasPerformanceData,
    label,
    onSelectGapAnalysisPeriod,
    periodSelection,
    selectedPeriod,
}: ReportPeriodColumnProps): JSX.Element => {
    const periodSelectionLabel =
        periodSelection?.performance_period_label ?? null;
    const selectedPeriodLabel =
        selectedPeriod?.performance_period_label ?? null;
    const isSelected =
        selectedPeriodLabel !== null &&
        selectedPeriodLabel === periodSelectionLabel;
    const dateRanges = getGapDateRangesFromPeriodSelection(periodSelection);

    return (
        <div
            className={cx('border flex-none bg-offwhite', {
                'border-tertiary-200': !isSelected,
                'border-primary-500': isSelected,
            })}
            style={{ width: columnWidth }}
        >
            <p
                className="flex flex-col justify-center items-center"
                style={{ height: rowHeight }}
            >
                {label}
            </p>
            <PerformanceBar
                hasPerformanceData={hasPerformanceData}
                isSelected={isSelected}
            />
            <DetectionBar
                detections={dateRanges.detections}
                isSelected={isSelected}
                performancePeriod={dateRanges.performancePeriod}
            />
            <div
                className="flex justify-center items-end"
                style={{ height: rowHeight }}
            >
                <div className="mb-2 relative" style={{ left: 22, bottom: 5 }}>
                    <RadioButton
                        isChecked={isSelected}
                        handleChange={(update: RadioButtonUpdate): void => {
                            if (update.checked) {
                                onSelectGapAnalysisPeriod({
                                    hasDetectionData,
                                    hasPerformanceData,
                                    nextSelectedPeriod: periodSelection,
                                });
                            }
                        }}
                        labelSrOnly={true}
                        groupDirection="horizontal"
                        option={{
                            inputId: label,
                            label,
                            value: label,
                        }}
                    />
                </div>
            </div>
        </div>
    );
};
